import React, {useState} from 'react';

import {
  IconVideoOn,
  IconLocationGPS,
  IconPhone,
  IconMSTeamsYes,
} from '@zoom/sun-ui-icon/dist/cjs/index.js';
import {useTranslation} from 'react-i18next';

import {IconContainer} from './Style';

import {ReactComponent as GoogleMeetIcon} from 'assets/svg/google-meet.svg';
import {ZmQuaternaryBtn} from 'Components/ZmButtons';
import {ADD_ON_TYPE} from 'Utils/consts';
import {emailFieldSnippetIconStyle, EmailFieldSnippetText} from 'Utils/emailHtmlUtils';
import zoomPalette from 'zoomPalette';

const defaultInlineLocationIcon = <IconLocationGPS size={16} style={emailFieldSnippetIconStyle} />;
const inlineIconMap = {
  [ADD_ON_TYPE.ZOOM_MEETING]: {
    title: 'common.zoomMeeting',
    icon: <IconVideoOn color={zoomPalette.gray[60]} size={16} style={emailFieldSnippetIconStyle} />,
  },
  [ADD_ON_TYPE.GOOGLE_MEETING]: {
    title: 'integration.googleMeetTitle',
    icon: <span style={{...emailFieldSnippetIconStyle, fontSize: '16px'}}>
      <GoogleMeetIcon style={{width: '16px', height: '16px'}} />
    </span>,
  },
  [ADD_ON_TYPE.TEAMS_MEETING]: {
    title: 'integration.teamsMeetTitle',
    icon: <IconMSTeamsYes size={16} style={emailFieldSnippetIconStyle} />,
  },
  [ADD_ON_TYPE.IN_BOUND_CALL]: {
    title: 'apptForm.phoneCall',
    icon: <IconPhone size={16} style={emailFieldSnippetIconStyle} />,
  },
  [ADD_ON_TYPE.OUT_BOUND_CALL]: {
    title: 'apptForm.phoneCall',
    icon: <IconPhone size={16} style={emailFieldSnippetIconStyle} />,
  },
};

/**
 * Meeting type Component
 * @param {Object} props
 * @param {string} props.addOnType
 * @param {string} props.location
 * @return {ReactElement}
 */
export function InlineStyleMeetingType(props) {
  const {
    addOnType,
    location,
    locationAdditionalInfo,
  } = props;
  const {t} = useTranslation();

  const meetingType = addOnType || ADD_ON_TYPE.OFFLINE;
  // Backward compatibility with old data: Zoom Meeting may coexist with custom location.
  const zoomMeetingWithLocation =
    meetingType === ADD_ON_TYPE.ZOOM_MEETING && location && !location.startsWith('https');

  const displayText = location || t(iconMap[meetingType]?.title);

  return (
    displayText && <>
      {zoomMeetingWithLocation ?
        <>
          <EmailFieldSnippetText>
            {inlineIconMap[meetingType]?.icon}&nbsp;
            <span style={{marginLeft: '4px'}}>
              {t(inlineIconMap[meetingType]?.title)}
            </span>
            <span>&nbsp;+&nbsp;</span>
            {defaultInlineLocationIcon}&nbsp;
            <span style={{marginLeft: '4px'}}>{location}</span>
          </EmailFieldSnippetText>
        </> :
        <>
          {inlineIconMap[meetingType]?.icon || defaultInlineLocationIcon}&nbsp;
          <EmailFieldSnippetText
            style={{
              marginLeft: '6px',
              wordBreak: 'break-all',
              whiteSpace: 'wrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayText}
          </EmailFieldSnippetText>
        </>
      }

      {locationAdditionalInfo && <div style={{
        padding: '5px 0 0 19px',
        fontSize: '13px',
        color: '#6E7680',
      }}>
        {locationAdditionalInfo}
      </div>}
    </>
  );
}

const defaultLocationIcon = <IconLocationGPS size={14} />;

const iconMap = {
  [ADD_ON_TYPE.ZOOM_MEETING]: {
    title: 'common.zoomMeeting',
    icon: <IconVideoOn size={16} />,
  },
  [ADD_ON_TYPE.GOOGLE_MEETING]: {
    title: 'integration.googleMeetTitle',
    icon: <GoogleMeetIcon style={{width: '16px', height: '16px'}} />,
  },
  [ADD_ON_TYPE.TEAMS_MEETING]: {
    title: 'integration.teamsMeetTitle',
    icon: <IconMSTeamsYes size={16} />,
  },
  [ADD_ON_TYPE.IN_BOUND_CALL]: {
    title: 'apptForm.phoneCall',
    icon: <IconPhone size={16} />,
  },
  [ADD_ON_TYPE.OUT_BOUND_CALL]: {
    title: 'apptForm.phoneCall',
    icon: <IconPhone size={16} />,
  },
};

/**
 * Meeting type Component
 * @param {Object} props
 * @param {String} props.addOnType
 * @param {String} props.location
 * @return {ReactElement}
 */
export default function MeetingType(props) {
  const {
    addOnType,
    location,
    locationAdditionalInfo,
  } = props;
  const {t} = useTranslation();

  const meetingType = addOnType || ADD_ON_TYPE.OFFLINE;
  // Backward compatibility with old data: Zoom Meeting may coexist with custom location.
  const zoomMeetingWithLocation =
    meetingType === ADD_ON_TYPE.ZOOM_MEETING && location && !location.startsWith('https');

  const displayText = location || t(iconMap[meetingType]?.title);

  const isLongText = displayText?.length > 100;
  // teams meeting link can be very long, add a collapse/expand effect
  const [locationExpand, setLocationExpand] = useState(!isLongText);

  return (
    displayText && <>
      {zoomMeetingWithLocation?
        <IconContainer>
          {iconMap[meetingType]?.icon}
          <span >{t(iconMap[meetingType]?.title)}</span>
          <span>&nbsp;+&nbsp;</span> {defaultLocationIcon}
          <span style={{marginLeft: '4px'}}>{location}</span>
        </IconContainer>:
        <IconContainer sx={{alignItems: (isLongText && locationExpand)? 'flex-start': 'center'}}>
          {iconMap[meetingType]?.icon || defaultLocationIcon}
          <span style={{
            wordBreak: 'break-all',
            whiteSpace: locationExpand? 'wrap': 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {displayText}
          </span>
          {isLongText && <ZmQuaternaryBtn size="small" onClick={() => setLocationExpand((prev) => !prev)}>
            {t(locationExpand ? 'apptForm.hideDetails' : 'apptForm.viewDetails')}
          </ZmQuaternaryBtn>}
        </IconContainer>
      }
      {locationAdditionalInfo && <div style={{
        padding: '5px 0 0 19px',
        fontSize: '13px',
        color: '#6E7680',
      }}>
        {locationAdditionalInfo}
      </div>}
    </>
  );
}
